/* Offline-first attendance sync queue.
 *
 * Contract:
 *   - Every punch is first written to IndexedDB (see lib/db.js).
 *   - We try to upload immediately; on failure we mark PENDING_SYNC and retry.
 *   - The server endpoint /api/attendance/mobile/pwa-sync is idempotent on `local_id`.
 *   - Payroll truth is ONLY granted by the server after validation.
 */
import axios from "axios";
import { API_BASE, getPwaToken, clearPwaSession } from "./api";
import {
  enqueueAttendance, updateAttendance, listPending, getAttendance,
} from "./db";

const SYNC_URL = `${API_BASE}/attendance/mobile/pwa-sync`;

// Simple device fingerprint — stable per install.
function deviceFingerprint() {
  let fp = localStorage.getItem("presence_fp");
  if (!fp) {
    fp = "pwa_" + (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2));
    localStorage.setItem("presence_fp", fp);
  }
  return fp;
}

function newLocalId() {
  return "pwa_" + (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2));
}

export async function capturePunch({
  employee_id,
  outlet_id,
  attendance_type,
  selfie_blob = null,
  gps = null,
  employee_pin = null,
}) {
  const rec = {
    local_id: newLocalId(),
    employee_id,
    outlet_id,
    attendance_type,
    captured_at_device: new Date().toISOString(),
    gps_lat: gps?.lat ?? null,
    gps_lng: gps?.lng ?? null,
    gps_accuracy_m: gps?.accuracy ?? null,
    selfie_blob, // base64 data URL
    device_fingerprint: deviceFingerprint(),
    employee_pin, // hashed server-side, kept local only as verification token
    source: "PWA",
    sync_status: "PENDING",
    retry_count: 0,
    last_error: null,
    server_response: null,
    online_at_capture: navigator.onLine,
  };
  await enqueueAttendance(rec);
  // fire-and-forget upload; caller decides UI based on result
  return trySync(rec.local_id);
}

export async function trySync(local_id) {
  const rec = await getAttendance(local_id);
  if (!rec) return { ok: false, error: "not-found" };
  if (rec.sync_status === "SYNCED") return { ok: true, record: rec };
  if (!navigator.onLine) {
    await updateAttendance(local_id, { sync_status: "PENDING", last_error: "offline" });
    return { ok: false, error: "offline" };
  }
  try {
    const payload = {
      local_id: rec.local_id,
      employee_id: rec.employee_id,
      outlet_id: rec.outlet_id,
      attendance_type: rec.attendance_type,
      captured_at_device: rec.captured_at_device,
      gps_lat: rec.gps_lat,
      gps_lng: rec.gps_lng,
      gps_accuracy_m: rec.gps_accuracy_m,
      selfie: rec.selfie_blob,
      device_fingerprint: rec.device_fingerprint,
      source: rec.source,
      employee_pin: rec.employee_pin,
      online_at_capture: rec.online_at_capture,
    };
    const token = getPwaToken();
    if (!token) {
      await updateAttendance(local_id, {
        sync_status: "PENDING",
        last_error: "needs_enrolment",
      });
      return { ok: false, error: "needs_enrolment" };
    }
    const r = await axios.post(SYNC_URL, payload, {
      timeout: 12000,
      headers: { Authorization: `Bearer ${token}` },
    });
    const updated = await updateAttendance(local_id, {
      sync_status: "SYNCED",
      server_response: r.data,
      last_error: null,
    });
    return { ok: true, record: updated };
  } catch (e) {
    const retry = (await getAttendance(local_id))?.retry_count ?? 0;
    const patch = {
      retry_count: retry + 1,
      last_error: e?.response?.data?.detail || e.message || "upload_failed",
    };
    const status = e?.response?.status;
    // 401 → token expired; force re-enrolment next time and keep record PENDING.
    if (status === 401) {
      clearPwaSession();
      patch.sync_status = "PENDING";
      patch.last_error = "pwa_token_expired_reauth_required";
    } else if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
      patch.sync_status = "FAILED";
    } else {
      patch.sync_status = "PENDING";
    }
    const updated = await updateAttendance(local_id, patch);
    return { ok: false, error: patch.last_error, record: updated };
  }
}

export async function flushQueue() {
  const pending = await listPending();
  const results = [];
  // Sequential to avoid hammering server
  for (const p of pending) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await trySync(p.local_id));
  }
  return results;
}

// Event wiring — call once from the top-level component.
let listenersBound = false;
export function bindAutoSync(onChange) {
  if (listenersBound) return;
  listenersBound = true;
  const trigger = async () => {
    await flushQueue();
    onChange && onChange();
  };
  window.addEventListener("online", trigger);
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (e) => {
      if (e.data?.type === "FLUSH_QUEUE") trigger();
    });
  }
  // Also retry every 30s when tab visible
  setInterval(() => {
    if (document.visibilityState === "visible" && navigator.onLine) trigger();
  }, 30000);
}
